import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const HandbookPage = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const sections = [
    {
      id: 'mission',
      title: 'Mission & Philosophy',
      content: [
        'The East End Prep Athletic Department is committed to providing a comprehensive athletic program that promotes the physical, mental, and social development of our student-athletes.',
        'We believe that participation in athletics teaches valuable life lessons including teamwork, discipline, perseverance, and sportsmanship.',
        'Our goal is to create an environment where student-athletes can excel both on the field and in the classroom while developing character and leadership skills.'
      ]
    },
    {
      id: 'eligibility',
      title: 'Academic Eligibility Requirements',
      content: [
        'Student-athletes must maintain a minimum GPA of 2.0 to participate in athletics.',
        'Students failing two or more classes are ineligible until grades improve.',
        'Progress reports are reviewed weekly, and academic support is provided as needed.',
        'Students must be enrolled in at least 5 classes to maintain eligibility.',
        'Summer school completion may be required for continued participation.'
      ]
    },
    {
      id: 'conduct',
      title: 'Code of Conduct',
      content: [
        'Student-athletes are expected to demonstrate exemplary behavior both on and off campus.',
        'Respect for coaches, teammates, officials, and opponents is mandatory.',
        'Use of tobacco, alcohol, or illegal substances will result in immediate suspension.',
        'Social media conduct must reflect positively on the school and athletic program.',
        'Academic dishonesty or disciplinary issues may result in athletic suspension.'
      ]
    },
    {
      id: 'attendance',
      title: 'Attendance Policy',
      content: [
        'Regular attendance at practices and games is mandatory.',
        'Unexcused absences from practice may result in reduced playing time.',
        'Students must attend school on game days to be eligible to participate.',
        'Medical excuses must be provided by a licensed physician.',
        'Family emergencies should be communicated to coaches as soon as possible.'
      ]
    },
    {
      id: 'equipment',
      title: 'Equipment & Uniforms',
      content: [
        'All equipment issued by the school must be returned in good condition.',
        'Students are responsible for the cost of lost or damaged equipment.',
        'Personal protective equipment must meet safety standards.',
        'Uniforms should be worn with pride and kept clean and presentable.',
        'Equipment check-out and return dates will be communicated by coaches.'
      ]
    },
    {
      id: 'transportation',
      title: 'Transportation Policy',
      content: [
        'Students must travel to and from away games on school-provided transportation.',
        'Parents may request to transport their own child with written permission.',
        'Students may only ride home with parents or designated guardians.',
        'Behavior on team transportation must meet school conduct standards.',
        'Students are responsible for their belongings during travel.'
      ]
    },
    {
      id: 'injuries',
      title: 'Injury & Medical Procedures',
      content: [
        'All injuries must be reported to coaches immediately.',
        'Medical clearance is required before returning to play after injury.',
        'Athletic trainers are available for injury assessment and treatment.',
        'Emergency contact information must be current and accessible.',
        'Insurance information should be provided to the athletic department.'
      ]
    },
    {
      id: 'communication',
      title: 'Communication Guidelines',
      content: [
        'Parents should communicate concerns directly with coaches.',
        'The 24-hour rule applies - wait 24 hours after games before discussing concerns.',
        'Athletic Director should be contacted if issues cannot be resolved with coaches.',
        'Team meetings and parent meetings will be scheduled as needed.',
        'Emergency communications will be sent via email and text alerts.'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-primary text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="mb-4">
            <Link to="/family-resources" className="text-blue-200 hover:text-white">
              Family Resources
            </Link>
            <span className="mx-2 text-blue-200">/</span>
            <span className="text-white">Student-Athlete Handbook</span>
          </nav>
          <h1 className="text-4xl font-bold mb-4">
            Student-Athlete Handbook
          </h1>
          <p className="text-xl text-blue-100">
            Policies, procedures, and expectations for East End Prep student-athletes and families.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Introduction */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Welcome to East End Prep Athletics
          </h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            This handbook serves as your comprehensive guide to the East End Prep Athletic Program. 
            Please review all sections carefully with your student-athlete and keep this information 
            readily available throughout the athletic season.
          </p>
          <p className="text-gray-600 leading-relaxed">
            By participating in East End Prep athletics, student-athletes and parents agree to 
            abide by all policies and procedures outlined in this handbook.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {sections.map((section) => (
            <div key={section.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
              >
                <h3 className="text-lg font-semibold text-gray-900">
                  {section.title}
                </h3>
                {expandedSections[section.id] ? (
                  <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronRightIcon className="w-5 h-5 text-gray-500" />
                )}
              </button>
              
              {expandedSections[section.id] && (
                <div className="px-6 pb-6">
                  <div className="border-t border-gray-200 pt-4">
                    <ul className="space-y-3">
                      {section.content.map((item, index) => (
                        <li key={index} className="flex items-start">
                          <span className="flex-shrink-0 w-2 h-2 bg-primary rounded-full mt-2 mr-3"></span>
                          <span className="text-gray-600 leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="bg-white rounded-lg shadow-md p-8 mt-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            Questions or Concerns?
          </h3>
          <p className="text-gray-600 mb-6">
            If you have any questions about the policies outlined in this handbook, 
            please don't hesitate to contact the Athletic Department.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact"
              className="btn-primary inline-flex items-center justify-center"
            >
              Contact Athletic Department
            </Link>
            <a
              href="mailto:athletics@eastendprep.edu"
              className="btn-secondary inline-flex items-center justify-center"
            >
              Email Athletics Director
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HandbookPage;